import { useMemo } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { MatchPublic, StadiumPublic } from '@mondy/shared';
import { isMatchLive, liveMinuteLabel, matchPausedLabel } from '@mondy/shared';
import { api } from '@/api';
import { Spinner } from '@/components/Spinner';
import { TeamFlag } from '@/components/TeamFlag';
import { formatKickoffDateShort, formatKickoffShort, isKickoffToday } from '@/lib/format';

interface StadiumListResponse {
  stadiums: StadiumPublic[];
}

interface MatchListResponse {
  matches: MatchPublic[];
}

// Host countries → ISO 3166-1 alpha-2 for flagcdn. The seed only ever produces
// these three; an unknown country just renders without a flag.
const COUNTRY_FLAG: Record<string, string> = {
  USA: 'us',
  Canada: 'ca',
  Mexico: 'mx',
};

export function StadiumsPage() {
  const query = useQuery({
    queryKey: ['stadiums'],
    queryFn: () => api.get<StadiumListResponse>('/api/stadiums'),
    staleTime: 60 * 60_000, // stadiums almost never change
  });

  // Poll live matches so each stadium card can surface the game in progress
  // there. Identical queryKey to <StandingsSidebar/> — TanStack dedupes by key,
  // so this shares that poll wherever the sidebar is mounted (no extra network).
  const liveQuery = useQuery({
    queryKey: ['matches', 'live'],
    queryFn: () => api.get<MatchListResponse>('/api/matches/live'),
    refetchInterval: 20_000,
  });

  // Full fixture list, for the non-live fallback (next upcoming / last played).
  // No poll — these change rarely; the live poll above covers the volatile case
  // (matches Schedule's fetch-once behaviour).
  const allQuery = useQuery({
    queryKey: ['matches', 'all'],
    queryFn: () => api.get<MatchListResponse>('/api/matches'),
  });

  // Map each live match to the stadium it's played at. Matches whose venue
  // didn't link to a curated stadium (stadium === null) simply don't appear.
  const liveByStadium = useMemo(() => {
    const map = new Map<string, MatchPublic>();
    for (const m of liveQuery.data?.matches ?? []) {
      if (m.stadium?.id) map.set(m.stadium.id, m);
    }
    return map;
  }, [liveQuery.data]);

  // Per-stadium fallbacks: the earliest scheduled match (next upcoming) and the
  // latest finished match (last played). kickoffUtc is an ISO-UTC string, so
  // lexicographic comparison is chronological. Pure function of the fixture
  // list — `isKickoffToday` is evaluated fresh at render, not captured here.
  const fallbackByStadium = useMemo(() => {
    const upcoming = new Map<string, MatchPublic>();
    const lastPlayed = new Map<string, MatchPublic>();
    for (const m of allQuery.data?.matches ?? []) {
      const sid = m.stadium?.id;
      if (!sid) continue;
      if (m.status === 'scheduled') {
        const cur = upcoming.get(sid);
        if (!cur || m.kickoffUtc < cur.kickoffUtc) upcoming.set(sid, m);
      } else if (m.status === 'finished') {
        const cur = lastPlayed.get(sid);
        if (!cur || m.kickoffUtc > cur.kickoffUtc) lastPlayed.set(sid, m);
      }
    }
    return { upcoming, lastPlayed };
  }, [allQuery.data]);

  // The API returns stadiums pre-sorted by country then name, so a Map keyed by
  // country groups them contiguously while preserving that order.
  const byCountry = useMemo(() => {
    const map = new Map<string, StadiumPublic[]>();
    for (const s of query.data?.stadiums ?? []) {
      const arr = map.get(s.country);
      if (arr) arr.push(s);
      else map.set(s.country, [s]);
    }
    return Array.from(map.entries());
  }, [query.data]);

  return (
    <>
      <div className="page-head">
        <div className="page-title">
          <h1>
            The <span className="emph">stadiums</span>.
          </h1>
        </div>
      </div>

      {query.isLoading ? (
        <Spinner fullScreen label="Loading stadiums" />
      ) : query.isError ? (
        <p
          style={{
            padding: '48px 0',
            textAlign: 'center',
            fontSize: 13,
            color: 'var(--danger)',
          }}
        >
          Couldn't load stadiums.
        </p>
      ) : (
        <div className="flex flex-col gap-8">
          {byCountry.map(([country, stadiums]) => (
            <section key={country} className="flex flex-col gap-3">
              <header className="flex items-center gap-3">
                {COUNTRY_FLAG[country] ? (
                  <img
                    src={`https://flagcdn.com/w80/${COUNTRY_FLAG[country]}.png`}
                    alt=""
                    width={32}
                    height={21}
                    style={{ borderRadius: 3, border: '1px solid var(--line)' }}
                  />
                ) : null}
                <div className="bebas" style={{ fontSize: 28, letterSpacing: '0.04em' }}>
                  {country}
                </div>
              </header>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 14,
                }}
              >
                {stadiums.map((s) => (
                  <StadiumCard
                    key={s.id}
                    stadium={s}
                    match={
                      liveByStadium.get(s.id) ??
                      fallbackByStadium.upcoming.get(s.id) ??
                      fallbackByStadium.lastPlayed.get(s.id)
                    }
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}

function StadiumCard({ stadium: s, match }: { stadium: StadiumPublic; match?: MatchPublic }) {
  return (
    <Link
      to={`/stadiums/${s.id}`}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      {s.imageUrl ? (
        <div
          className="stadium-photo"
          style={{ aspectRatio: '16 / 9', backgroundImage: `url(${s.imageUrl})` }}
        />
      ) : null}
      {match ? <StadiumMatchStrip m={match} /> : null}
      <div className="flex flex-col gap-1 p-4">
        <div style={{ fontWeight: 700, fontSize: 16 }}>{s.name}</div>
        <div className="text-sm text-[color:var(--color-fg-muted)]">
          {s.city ? `${s.city}, ${s.country}` : s.country}
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-[color:var(--color-fg-muted)]">
          {s.capacity ? <span>{s.capacity.toLocaleString()} seats</span> : null}
          {s.capacity ? <span aria-hidden>·</span> : null}
          <span>
            {s.matchCount} match{s.matchCount === 1 ? '' : 'es'}
          </span>
        </div>
      </div>
    </Link>
  );
}

// The most relevant match for a stadium, shown as a banner on its card. One of
// three states, picked by the parent (live > next upcoming > last played):
//   live/paused → hot strip with the running score + minute (or HALF-TIME)
//   scheduled   → neutral strip with the kick-off time (date in the caption
//                 unless it's today, per the spec)
//   finished    → neutral strip with the final score + a Full time/aet/Pens note
// Reuses <StandingsSidebar/>'s LiveGameRow .lr-* styles; .is-static drops the
// hot tint for the non-live states.
function StadiumMatchStrip({ m }: { m: MatchPublic }) {
  const live = isMatchLive(m);
  const finished = m.status === 'finished';
  const paused = m.status === 'paused';
  // A freshly kicked-off 0–0 has no result row yet — fall back to 0–0 so a live
  // strip still renders rather than blanking out.
  const main = m.result?.extraTime ?? m.result?.fullTime ?? { team1: 0, team2: 0 };
  const pens = m.result?.penalties ?? null;
  // Codes fall back to the round placeholder ("1A", "W73") for unresolved
  // knockout fixtures, which is exactly where an upcoming match can appear.
  const code1 = m.team1?.fifaCode ?? m.team1Placeholder ?? '—';
  const code2 = m.team2?.fifaCode ?? m.team2Placeholder ?? '—';

  let status;
  if (live) {
    status = paused ? (
      <span className="lr-status paused">{matchPausedLabel(m.minute)}</span>
    ) : (
      <span className="lr-status">
        <span className="live-dot" />
        LIVE
        {typeof m.minute === 'number' ? (
          <span className="lr-minute">{liveMinuteLabel(m.minute, m.addedTime)}</span>
        ) : null}
      </span>
    );
  } else if (finished) {
    const note = pens
      ? `Pens ${pens.team1}–${pens.team2}`
      : m.result?.extraTime
        ? 'a.e.t.'
        : 'Full time';
    status = <span className="lr-status paused">{note}</span>;
  } else {
    status = (
      <span className="lr-status paused">
        {isKickoffToday(m.kickoffUtc) ? 'Kick-off' : formatKickoffDateShort(m.kickoffUtc)}
      </span>
    );
  }

  let center;
  if (live) {
    center = (
      <div className="lr-score tabular">
        {pens ? <span className="lr-pens">({pens.team1})</span> : null}
        {main.team1}–{main.team2}
        {pens ? <span className="lr-pens">({pens.team2})</span> : null}
      </div>
    );
  } else if (finished) {
    center = (
      <div className="lr-score tabular">
        {main.team1}–{main.team2}
      </div>
    );
  } else {
    center = <div className="lr-score tabular">{formatKickoffShort(m.kickoffUtc)}</div>;
  }

  return (
    <div className={`live-row stadium-live${live ? '' : ' is-static'}`}>
      <div className="lr-top">
        {status}
        <span className="lr-stage">
          {m.group ? `Grp ${m.group}` : m.stage.toUpperCase()}
        </span>
      </div>
      <div className="lr-teams">
        <div className="lr-team">
          <TeamFlag code={m.team1?.fifaCode ?? null} url={m.team1?.flagUrl ?? null} size="xs" />
          <span className="lr-code">{code1}</span>
        </div>
        {center}
        <div className="lr-team right">
          <span className="lr-code">{code2}</span>
          <TeamFlag code={m.team2?.fifaCode ?? null} url={m.team2?.flagUrl ?? null} size="xs" />
        </div>
      </div>
    </div>
  );
}
